import * as z from 'zod';

import { EVENT_TYPE, MAIN_IDENTIFIER_TYPE } from 'src/features/eventLog/types';
import {
    BANKNOTE_VALIDATOR_MANUFACTURER,
    CCTALK_INTERFACE_MODAL,
    COIN_HOPPER_MANUFACTURER,
    COIN_MECHANISM_ACCEPTOR_MANUFACTURER,
    COMMUNICATOR_TYPE,
    GATEWAY_DISPLAY_MODEL,
    IDLE_STATE,
    LISTEN_STATE,
    MEMBERSHIP_CATEGORY,
    ORIENTATION,
    SYSTEM_COMPONENT,
    SYSTEM_SETTINGS_CATEGORY,
    SYSTEM_SETTINGS_CODE,
} from 'src/features/systems/utils/types';
import { validationMessagesMap } from 'src/utils/messages';

export type SystemSettingCreateFormSchemaValues = z.infer<typeof systemSettingCreateFormSchema>;
export type SystemSettingFormSchemaValues = z.infer<typeof formSchema>;

export const systemSettingCreateFormSchema = z.object({
    category: z.enum(SYSTEM_SETTINGS_CATEGORY),
    code: z.enum(SYSTEM_SETTINGS_CODE),
    value: z.union([
        z.array(z.enum(MAIN_IDENTIFIER_TYPE)).min(1),
        z.enum(MAIN_IDENTIFIER_TYPE),
        z.array(z.enum(MEMBERSHIP_CATEGORY)).min(1),
        z.array(z.enum(EVENT_TYPE)).min(1),
    ]),
});

export const formSchema = z.object({
    supported_identifier_type: z.array(z.enum(MAIN_IDENTIFIER_TYPE)).min(1),
    main_identifier_type: z.enum(MAIN_IDENTIFIER_TYPE),
    enabled_membership_categories: z.array(z.enum(MEMBERSHIP_CATEGORY)).min(1),
    enabled_event_types: z.array(z.enum(EVENT_TYPE)).min(1),
});

export type SystemComponentFormSchemaValues = z.infer<typeof systemComponentFormSchema>;

const commonComponentDataSchema = z.object({
    type: z.string().nonempty(),
    make: z.string().nonempty(),
    model: z.string().nonempty(),
    serial_number: z.string().nonempty(),
});

const otherCommonComponentDataSchema = z.object({
    model: z.string().nonempty(),
    serial_number: z.string().nonempty(),
    firmware_version: z.string().nonempty(),
});

const cameraComponentDataSchema = z
    .object({
        rtsp_stream_url: z.string(),
    })
    .and(commonComponentDataSchema);

const communicatorDataSchema = z
    .object({
        channel: z.number(),
        topic_code: z.string().optional(),
    })
    .and(commonComponentDataSchema);

const gatewayCommunicatorDataSchema = z
    .object({
        persistent_channel: z.number(),
        open_duration: z.number(),
        status_listen_state: z.enum(LISTEN_STATE).optional(),
        status_channel: z.int().optional(),
    })
    .and(communicatorDataSchema)
    .superRefine((data, ctx) => {
        if (data.type === COMMUNICATOR_TYPE.SP_MQTT && !data.status_listen_state) {
            ctx.addIssue({
                path: ['status_listen_state'],
                message: validationMessagesMap.validationErrorRequired,
                code: 'custom',
            });
        }
        if (data.type === COMMUNICATOR_TYPE.SP_MQTT && !data.status_channel) {
            ctx.addIssue({
                path: ['status_channel'],
                message: validationMessagesMap.validationErrorRequired,
                code: 'custom',
            });
        }
    });

const sensorReaderCommunicatorDataSchema = z
    .object({
        listen_state: z.enum(LISTEN_STATE).optional(),
    })
    .and(communicatorDataSchema)
    .superRefine((data, ctx) => {
        if (data.type === COMMUNICATOR_TYPE.SP_MQTT && !data.listen_state) {
            ctx.addIssue({
                path: ['listen_state'],
                message: validationMessagesMap.validationErrorRequired,
                code: 'custom',
            });
        }
    });

const gatewayDisplayDataSchema = z
    .object({
        display_server_ip: z.string().optional(),
        display_server_api_key: z.string().optional(),
        screen_width: z.int().optional(),
        screen_height: z.int().optional(),
        idle_state: z.enum(IDLE_STATE).optional(),
        orientation: z.enum(ORIENTATION).optional(),
        display_model: z.enum(GATEWAY_DISPLAY_MODEL),
    })
    .superRefine((data, ctx) => {
        type Data = typeof data;

        const requiredFieldsByModel: Record<string, (keyof Data)[]> = {
            [GATEWAY_DISPLAY_MODEL.SP]: ['display_server_ip', 'display_server_api_key', 'idle_state', 'orientation'],
            [GATEWAY_DISPLAY_MODEL.HUIDU]: ['display_server_ip', 'screen_width', 'screen_height'],
            [GATEWAY_DISPLAY_MODEL.NED200]: ['display_server_ip'],
        };

        const requiredFields = requiredFieldsByModel[data.display_model] || [];

        requiredFields.forEach((field) => {
            if (!data[field]) {
                ctx.addIssue({
                    path: [field],
                    message: validationMessagesMap.validationErrorRequired,
                    code: 'custom',
                });
            }
        });
    });

const banknoteValidatorDataSchema = z
    .object({
        manufacturer: z.enum(BANKNOTE_VALIDATOR_MANUFACTURER),
        currency_dataset: z.string(),
    })
    .and(otherCommonComponentDataSchema);

const coinHopperDataSchema = z
    .object({
        manufacturer: z.enum(COIN_HOPPER_MANUFACTURER),
        currency_dataset: z.string(),
    })
    .and(otherCommonComponentDataSchema);

const coinMechanismCoinAcceptorDataSchema = z
    .object({
        manufacturer: z.enum(COIN_MECHANISM_ACCEPTOR_MANUFACTURER),
        currency_dataset: z.string(),
    })
    .and(otherCommonComponentDataSchema);

const cctalkInterfaceDataSchema = z.object({
    manufacturer: z.enum(COIN_MECHANISM_ACCEPTOR_MANUFACTURER),
    model: z.enum(CCTALK_INTERFACE_MODAL),
    serial_number: z.string(),
    firmware_version: z.string(),
});

const systemComponentUnion = z.discriminatedUnion('component', [
    z.object({
        component: z.literal(SYSTEM_COMPONENT.LPR_CAMERA),
        data: cameraComponentDataSchema,
    }),
    z.object({
        component: z.literal(SYSTEM_COMPONENT.GATEWAY_COMMUNICATOR),
        data: gatewayCommunicatorDataSchema,
    }),
    z.object({
        component: z.literal(SYSTEM_COMPONENT.GATEWAY_DISPLAY),
        data: gatewayDisplayDataSchema,
    }),
    z.object({
        component: z.literal(SYSTEM_COMPONENT.SENSOR_READER_COMMUNICATOR),
        data: sensorReaderCommunicatorDataSchema,
    }),
    z.object({
        component: z.literal(SYSTEM_COMPONENT.ACCESS_KEY_READER_COMMUNICATOR),
        data: communicatorDataSchema,
    }),
    z.object({
        component: z.literal(SYSTEM_COMPONENT.BANKNOTE_VALIDATOR),
        data: banknoteValidatorDataSchema,
    }),
    z.object({
        component: z.literal(SYSTEM_COMPONENT.COIN_HOPPER),
        data: coinHopperDataSchema,
    }),
    z.object({
        component: z.literal(SYSTEM_COMPONENT.COIN_MECHANISM),
        data: coinMechanismCoinAcceptorDataSchema,
    }),
    z.object({
        component: z.literal(SYSTEM_COMPONENT.COIN_ACCEPTOR),
        data: coinMechanismCoinAcceptorDataSchema,
    }),
    z.object({
        component: z.literal(SYSTEM_COMPONENT.CCTALK_INTERFACE),
        data: cctalkInterfaceDataSchema,
    }),
]);

const baseSystemComponentFormSchema = z.object({
    component_type: z.string(),
    component: z.enum([
        SYSTEM_COMPONENT.LPR_CAMERA,
        SYSTEM_COMPONENT.GATEWAY_COMMUNICATOR,
        SYSTEM_COMPONENT.ACCESS_KEY_READER_COMMUNICATOR,
        SYSTEM_COMPONENT.SENSOR_READER_COMMUNICATOR,
        SYSTEM_COMPONENT.BANKNOTE_VALIDATOR,
        SYSTEM_COMPONENT.COIN_HOPPER,
        SYSTEM_COMPONENT.COIN_MECHANISM,
        SYSTEM_COMPONENT.COIN_ACCEPTOR,
        SYSTEM_COMPONENT.CCTALK_INTERFACE,
        SYSTEM_COMPONENT.GATEWAY_DISPLAY,
    ]),
    name: z.string(),
    code: z.string(),
    zone: z.string(),
    gateway: z.string(),
    pay_station: z.string().optional(),
});
export const systemComponentFormSchema = baseSystemComponentFormSchema.and(systemComponentUnion);
